// ─── Insight Lens Copy ───────────────────────────────────────────────────────
//
// Translates a raw Insight (from src/lib/insights.ts) into lens-aware
// title + detail strings. The insights engine writes in clinical prose
// ("hs-CRP of 2.1 mg/L exceeds the 3.0 threshold linked to immunometabolic
// depression"). That's appropriate for clinicians but too technical for
// patients, so we provide a patient-facing variant keyed by insight id.
//
// The engine is NEVER modified — it keeps producing the same strings as
// today. This module is a pure read-only transformer that sits between
// the engine output and the UI layer. If an id is not in the lookup,
// we fall back to a regex sanitizer so the patient lens can never
// accidentally render raw clinical notation.
//
// Consumed by src/components/InsightsPanel.tsx.
// ──────────────────────────────────────────────────────────────────────────────

import type { Insight } from '../insights';
import type { Lens } from './types';

export interface InsightCopyPair {
  title: string;
  detail: string;
}

// ─── Patient variants keyed by insight id ────────────────────────────────────

/**
 * Map of insight id → (insight) → patient-facing copy. The function form lets
 * us pull dynamic values off the insight without having to reparse the
 * clinician detail string.
 *
 * Keys MUST stay in sync with src/lib/insights.ts — any new insight added
 * there should also get a patient variant here. The fallback path
 * (sanitizePatientText) covers unknown ids so the UI stays safe.
 */
const PATIENT_COPY: Record<string, (ins: Insight) => InsightCopyPair> = {
  // ─── Mood / PHQ-4 ──
  'phq4-improved': () => ({
    title: 'Your mood check-in is improving',
    detail: 'Your recent check-in scored lower than the one before it — a meaningful shift in the right direction.',
  }),
  'phq4-worsened': () => ({
    title: 'Your mood check-in has shifted',
    detail: 'Your recent check-in scored higher than the one before it. Worth keeping an eye on and talking about at your next visit.',
  }),

  // ─── Labs ──
  'lab-vitd': (ins) => {
    const deficient = ins.priority >= 8;
    return deficient
      ? {
          title: 'Vitamin D is low',
          detail: 'Low vitamin D can contribute to fatigue, low mood, and reduced immune resilience. Worth talking to your clinician about.',
        }
      : {
          title: 'Vitamin D is on the lower side',
          detail: 'Your vitamin D is slightly below the range most supportive of mood and energy. Often an easy fix.',
        };
  },
  'lab-b12': () => ({
    title: 'B12 may need attention',
    detail: 'Lower B12 can contribute to brain fog, fatigue, and changes in mood. Your clinician can help you interpret this.',
  }),
  'lab-crp': (ins) => {
    const high = ins.priority >= 8;
    return high
      ? {
          title: 'Inflammation looks elevated',
          detail: 'An inflammation marker is running higher than the typical healthy range. Sleep, movement, and diet choices can all help bring it down.',
        }
      : {
          title: 'Mild inflammation noticed',
          detail: 'An inflammation marker is slightly above the optimal range. Worth monitoring and revisiting at your next lab draw.',
        };
  },
  'lab-omega3': () => ({
    title: 'Omega-3 is low',
    detail: 'Omega-3 supports brain health and mood stability. A modest increase in dietary fatty fish or a supplement can help.',
  }),
  'lab-ferritin': () => ({
    title: 'Iron stores are low',
    detail: 'Low iron stores can drive fatigue and make it harder for your body to recover. Worth discussing with your clinician.',
  }),

  // ─── HRV ──
  'hrv-trend': (ins) => {
    const up = ins.direction === 'positive';
    return up
      ? {
          title: 'Your recovery is improving',
          detail: 'Your recovery signal is trending up this week — a sign your body is bouncing back well.',
        }
      : {
          title: 'Your recovery has dipped',
          detail: 'Your recovery signal is a bit lower than last week. Prioritize rest, sleep, and easier days when you can.',
        };
  },

  // ─── Sleep duration ──
  'sleep-duration': (ins) => {
    const up = ins.direction === 'positive';
    return up
      ? {
          title: 'You are sleeping more',
          detail: 'You are averaging more sleep per night than last week — keep the momentum going.',
        }
      : {
          title: 'Sleep has been shorter',
          detail: 'You are averaging less sleep per night than last week. Even an extra 20 minutes can help your mood and energy.',
        };
  },

  // ─── Sleep score ──
  'sleep-score': (ins) => {
    const up = ins.direction === 'positive';
    return up
      ? {
          title: 'Sleep quality is improving',
          detail: 'Your sleep quality score is trending up this week — your routine is working.',
        }
      : {
          title: 'Sleep quality has dipped',
          detail: 'Your sleep quality score is a bit lower than last week. A consistent bedtime and lower-stimulus evenings usually help.',
        };
  },

  // ─── Step streak ──
  'step-streak': (ins) => ({
    title: 'Nice movement streak',
    detail: `You have hit a solid step count ${ins.value} in a row — consistent movement really does support mood.`,
  }),

  // ─── Stress ──
  'stress-trend': (ins) => {
    const up = ins.direction === 'positive';
    return up
      ? {
          title: 'You are handling stress better',
          detail: 'Your body is spending less time in stressed states than last week.',
        }
      : {
          title: 'Stress has been running high',
          detail: 'Your body is showing more signs of stress this week. Extra recovery and protected sleep can help reset it.',
        };
  },

  // ─── Correlations ──
  'exercise-sleep': () => ({
    title: 'Exercise helps you sleep',
    detail: 'On days you move for 20+ minutes, you tend to sleep noticeably better.',
  }),
  'sleep-hrv': () => ({
    title: 'Good sleep helps you recover',
    detail: 'On nights you sleep well, your recovery signal the next day is higher.',
  }),
  'steps-stress': () => ({
    title: 'Movement eases your stress',
    detail: 'On days you get more steps, your body shows fewer signs of stress.',
  }),
};

// ─── Clinician variants ──────────────────────────────────────────────────────

/**
 * Clinician variant defaults to the raw engine string (already clinical).
 * A few ids get a slightly tightened clinician title for consistency with
 * the Pattern Interpretation + Primary Insight sections.
 */
const CLINICIAN_TITLE_OVERRIDES: Record<string, string> = {
  'phq4-improved': 'PHQ-4 score improved',
  'phq4-worsened': 'PHQ-4 score elevated',
  'hrv-trend':     '7-day HRV trend shift',
  'sleep-duration':'Weekly sleep duration delta',
  'sleep-score':   'Weekly sleep quality delta',
  'stress-trend':  'Stress-resilience score shift',
};

// ─── Fallback sanitizer ──────────────────────────────────────────────────────

/**
 * Fallback sanitizer for unknown insight ids on the patient lens. Strips
 * the obvious clinical markers so the patient never sees raw notation.
 * Real safety still comes from the PATIENT_COPY lookup above — this is
 * defense-in-depth only.
 *
 * Applied unconditionally (not gated on containsClinicalNotation) because
 * that helper's forbidden-pattern list uses `\bhsCRP\b` without the
 * hyphen and so misses `hs-CRP`. Running these replacements every time
 * is cheap and safer.
 */
function sanitizeInsightText(text: string): string {
  return text
    .replace(/PHQ-?\d/gi, 'your mood check-in')
    .replace(/GAD-?\d/gi, 'your anxiety check-in')
    .replace(/hs-?CRP/gi, 'inflammation marker')
    .replace(/HbA1c/gi, 'blood sugar marker')
    .replace(/\bTSH\b/g, 'thyroid marker')
    .replace(/\bng\/mL\b|\bpg\/mL\b|\bmg\/L\b|\bmg\/dL\b/gi, '')
    .replace(/\bHRV\b/g, 'recovery signal')
    .replace(/immunometabolic depression/gi, 'mood-related inflammation')
    .replace(/\s+/g, ' ')
    .trim();
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Returns lens-appropriate title + detail for an insight.
 *
 *   - patient:   looks up PATIENT_COPY[id], falls back to sanitized engine
 *                text with clinical markers stripped.
 *   - clinician: uses the engine's title + detail verbatim, optionally
 *                substituting a tightened title from CLINICIAN_TITLE_OVERRIDES.
 *
 * Pure. Safe to call in render.
 */
export function insightCopy(insight: Insight, lens: Lens): InsightCopyPair {
  if (lens === 'clinician') {
    return {
      title: CLINICIAN_TITLE_OVERRIDES[insight.id] ?? insight.title,
      detail: insight.detail,
    };
  }

  // Patient lens
  const builder = PATIENT_COPY[insight.id];
  if (builder) return builder(insight);

  // Unknown id — sanitize unconditionally so we never leak raw clinical
  // notation to the patient lens.
  return {
    title: sanitizeInsightText(insight.title),
    detail: sanitizeInsightText(insight.detail),
  };
}
